import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Cake, PartyPopper, Wallet, BookOpen, CalendarDays, TrendingUp, ArrowRightLeft, ArrowUpRight, UserCircle2, Briefcase, Award, ListTodo, CheckCircle2, Camera } from "lucide-react";
import PassportPhoto from "@/components/PassportPhoto";
import PassportPhotoUploader from "@/components/PassportPhotoUploader";
import HolidayCountdown from "@/components/HolidayCountdown";
import FaceEnrollPrompt from "@/components/FaceEnrollPrompt";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

function monthDay(iso) {
    if (!iso || iso.length < 10) return "";
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const LEAVE_PILL = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    hr_verified: "bg-indigo-50 text-indigo-700 border-indigo-200",
    approved: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    cancelled: "bg-ink/5 text-ink/70 border-ink/10",
};

const LOAN_PILL = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    accountant_verified: "bg-sky-50 text-sky-700 border-sky-200",
    dept_approved: "bg-violet-50 text-violet-700 border-violet-200",
    finance_approved: "bg-blue-50 text-blue-700 border-blue-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    disbursed: "bg-indigo-50 text-indigo-700 border-indigo-200",
    completed: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    cancelled: "bg-ink/5 text-ink/70 border-ink/10",
};

export default function EmployeeDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState({
        balance: null,
        leaves: [],
        loans: [],
        employee: null,
        recentHires: [],
        birthdays: [],
        anniversaries: [],
        monthName: "",
    });
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [passportOpen, setPassportOpen] = useState(false);

    const loadEmployee = async () => {
        try {
            const empId = user?.employee_id;
            if (empId) {
                const { data: emp } = await api.get(`/employees/${empId}`);
                setData((d) => ({ ...d, employee: emp }));
            }
        } catch {/* silent */}
    };

    const loadTasks = async () => {
        try {
            const { data: rows } = await api.get("/tasks");
            setTasks(rows || []);
        } catch {/* silent */}
    };

    const markTask = async (id, status) => {
        try {
            await api.put(`/tasks/${id}`, { status });
            toast.success(status === "done" ? "Marked done" : "Updated");
            loadTasks();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    useEffect(() => { loadTasks(); }, []);

    useEffect(() => {
        (async () => {
            try {
                const empId = user?.employee_id;
                const promises = [
                    api.get("/dashboard/stats").catch(() => ({ data: {} })),
                    api.get("/leaves", { params: { employee_id: empId } }).catch(() => ({ data: [] })),
                    api.get("/performance").catch(() => ({ data: [] })),
                ];
                if (empId) {
                    promises.push(api.get(`/employees/${empId}`).catch(() => ({ data: null })));
                    promises.push(api.get(`/employees/${empId}/loan-ledger`).catch(() => ({ data: null })));
                    promises.push(api.get(`/leaves/balance/${empId}`).catch(() => ({ data: null })));
                }
                const results = await Promise.all(promises);
                const stats = results[0]?.data || {};
                const leaves = results[1]?.data || [];
                const performance = results[2]?.data || [];
                const employee = results[3]?.data || null;
                const loanLedger = results[4]?.data || { loans: [], summary: {} };
                const balance = results[5]?.data || null;
                setData({
                    balance,
                    leaves,
                    loans: loanLedger.loans || [],
                    loanSummary: loanLedger.summary || {},
                    employee,
                    performance,
                    recentHires: stats.recent_hires || [],
                    birthdays: stats.birthdays || [],
                    anniversaries: stats.anniversaries || [],
                    monthName: stats.current_month_name || "",
                });
            } finally {
                setLoading(false);
            }
        })();
    }, [user?.employee_id]);

    const careerEvents = (() => {
        const emp = data.employee;
        if (!emp) return [];
        const out = [];
        if (emp.joining_date) {
            out.push({ kind: "joined", date: emp.joining_date, label: "Joined the company", detail: `${emp.position || ""}${emp.department ? ` · ${emp.department}` : ""}` });
        }
        (emp.salary_history || []).forEach((h) => {
            const prev = Number(h.previous_salary || 0);
            const cur = Number(h.new_salary || 0);
            const diff = cur - prev;
            out.push({
                kind: diff >= 0 ? "raise" : "adjustment",
                date: h.date,
                label: diff >= 0 ? "Salary increment" : "Salary adjustment",
                detail: `${fmt(prev)} → ${fmt(cur)} ${h.reason ? `· ${h.reason}` : ""}`,
            });
        });
        (emp.transfer_history || []).forEach((t) => {
            out.push({
                kind: "transfer",
                date: t.effective_date || t.transferred_at,
                label: "Inter-company transfer",
                detail: `${t.previous_company || "—"} → ${t.new_company || "—"}`,
            });
        });
        return out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    })();

    if (loading) {
        return <div className="px-5 sm:px-8 md:px-12 py-8 sm:py-12 text-sm text-ink/40">Loading your dashboard…</div>;
    }

    const emp = data.employee || {};
    const empName = emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || user?.name || "Welcome";

    return (
        <div className="px-5 sm:px-8 md:px-12 py-8 sm:py-12 md:py-16" data-testid="employee-dashboard">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div className="flex items-center gap-5">
                    <div className="relative shrink-0 group">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden ring-2 ring-klein/20 shadow-sm bg-ink/5">
                            <PassportPhoto
                                employee={emp}
                                className="w-full h-full object-cover"
                                initialsClassName="w-full h-full text-xl sm:text-2xl"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => setPassportOpen(true)}
                            className="absolute -bottom-1 -right-1 bg-klein text-white p-1.5 rounded-full shadow hover:bg-klein/90"
                            data-testid="emp-dashboard-passport-btn"
                            aria-label="Change passport photo"
                            title="Change passport photo"
                        >
                            <Camera className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    <div>
                        <div className="overline">Your portal</div>
                        <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                            Hello, {emp.first_name || "there"}.
                        </h1>
                        <p className="mt-3 text-sm text-ink/60 max-w-xl">
                            {emp.position && emp.department ? `${emp.position} · ${emp.department}` : "Welcome to your self-service portal."}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Link to="/leaves" className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90" data-testid="employee-quick-leave">
                        <CalendarDays className="w-4 h-4" /> Apply for leave
                    </Link>
                    <Link to="/loans" className="inline-flex items-center gap-2 border border-ink/20 px-5 py-3 text-sm hover:bg-ink hover:text-white" data-testid="employee-quick-loan">
                        <Wallet className="w-4 h-4" /> Request advance
                    </Link>
                    <Link to="/check-in" className="inline-flex items-center gap-2 border border-klein text-klein px-5 py-3 text-sm hover:bg-klein hover:text-white" data-testid="employee-quick-checkin">
                        Check in / out <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>

            {/* Top KPIs */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
                <KPI label="Leave balance" value={`${data.balance?.balance ?? "—"} days`} icon={CalendarDays} testid="emp-kpi-leave-balance" />
                <KPI label="Leave used" value={`${data.balance?.used ?? 0} days`} icon={CalendarDays} testid="emp-kpi-leave-used" />
                <KPI label="Outstanding loan" value={fmt(data.loanSummary?.total_outstanding)} icon={Wallet} testid="emp-kpi-outstanding-loan" />
                <KPI label="Loans / advances" value={`${(data.loans || []).length}`} icon={BookOpen} accent testid="emp-kpi-loan-count" />
            </div>

            <HolidayCountdown className="mt-8" />
            <FaceEnrollPrompt employeeId={user?.employee_id} />

            {/* Two-column layout */}
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                {/* Career timeline */}
                <div className="lg:col-span-2 bg-white p-8" data-testid="emp-career-timeline">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="overline">Your journey</div>
                            <h2 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-2">
                                <Briefcase className="w-5 h-5 text-klein" /> Career timeline
                            </h2>
                        </div>
                        <Link to={emp.id ? `/employees/${emp.id}` : "#"} className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-klein" data-testid="emp-view-profile-link">
                            View profile →
                        </Link>
                    </div>
                    {careerEvents.length === 0 ? (
                        <div className="text-sm text-ink/40 py-8 border-t border-ink/5">No events recorded yet.</div>
                    ) : (
                        <ol className="relative border-l border-ink/15 ml-3 space-y-5">
                            {careerEvents.map((ev, i) => {
                                const ICON = ev.kind === "transfer" ? ArrowRightLeft : ev.kind === "joined" ? UserCircle2 : TrendingUp;
                                const tone =
                                    ev.kind === "joined" ? "text-ink bg-ink"
                                    : ev.kind === "transfer" ? "text-white bg-klein"
                                    : ev.kind === "raise" ? "text-white bg-emerald-600"
                                    : "text-white bg-red-600";
                                return (
                                    <li key={i} className="ml-6" data-testid={`emp-timeline-${i}`}>
                                        <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${tone}`}>
                                            <ICON className="w-3 h-3 text-white" strokeWidth={2} />
                                        </span>
                                        <div className="text-[11px] uppercase tracking-[0.15em] text-ink/50">{ev.date || ""}</div>
                                        <div className="font-display font-bold text-ink">{ev.label}</div>
                                        <div className="text-xs text-ink/60 mt-0.5">{ev.detail}</div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>

                {/* Birthdays + anniversaries (company-wide) */}
                <div className="bg-white p-8" data-testid="emp-celebrations">
                    <div className="overline">Celebrations · {data.monthName}</div>
                    <h2 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-2">
                        <Cake className="w-5 h-5 text-klein" /> This month
                    </h2>
                    <div className="mt-5 space-y-1">
                        {data.birthdays.length === 0 && data.anniversaries.length === 0 && (
                            <div className="text-sm text-ink/40">No upcoming birthdays or anniversaries.</div>
                        )}
                        {data.birthdays.slice(0, 5).map((p) => (
                            <div key={`b-${p.id}`} className="flex items-center gap-3 py-2 border-b border-ink/5" data-testid={`emp-birthday-${p.id}`}>
                                <Cake className="w-3.5 h-3.5 text-klein shrink-0" />
                                <div className="text-sm text-ink truncate">{p.first_name} {p.last_name}</div>
                                <div className="ml-auto text-xs text-ink/50">{monthDay(p.date_of_birth)}</div>
                            </div>
                        ))}
                        {data.anniversaries.slice(0, 5).map((p) => (
                            <div key={`a-${p.id}`} className="flex items-center gap-3 py-2 border-b border-ink/5" data-testid={`emp-anniversary-${p.id}`}>
                                <PartyPopper className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                <div className="text-sm text-ink truncate">{p.first_name} {p.last_name}</div>
                                <div className="ml-auto text-xs text-ink/50">{p.years}y · {monthDay(p.joining_date)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* My Tasks */}
            <div className="mt-12 bg-white border border-ink/10 p-8" data-testid="emp-tasks-card">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="overline">Today&apos;s jobs</div>
                        <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-2">
                            <ListTodo className="w-5 h-5 text-klein" /> My tasks
                        </h3>
                    </div>
                    <Link to="/tasks" className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-klein" data-testid="emp-tasks-link">
                        View all →
                    </Link>
                </div>
                {tasks.filter((t) => t.status !== "done" && t.status !== "cancelled").length === 0 ? (
                    <div className="text-sm text-ink/40 py-4 border-t border-ink/5" data-testid="emp-tasks-empty">No open tasks. Nice work.</div>
                ) : (
                    <ul className="space-y-1">
                        {tasks.filter((t) => t.status !== "done" && t.status !== "cancelled").slice(0, 6).map((t) => {
                            const isLate = t.due_date && t.due_date < new Date().toISOString().slice(0, 10);
                            return (
                                <li key={t.id} className="flex items-center gap-3 py-2 border-b border-ink/5 text-sm" data-testid={`emp-task-${t.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-ink truncate font-medium">{t.title}</div>
                                        <div className="text-xs text-ink/50 truncate">
                                            Due {t.due_date || "—"} {isLate && <span className="text-red-600 font-medium ml-2">· Overdue</span>}
                                        </div>
                                    </div>
                                    {t.status === "pending" && (
                                        <button onClick={() => markTask(t.id, "in_progress")} className="text-xs px-2 py-1 border border-klein text-klein hover:bg-klein hover:text-white" data-testid={`emp-task-start-${t.id}`}>Start</button>
                                    )}
                                    <button onClick={() => markTask(t.id, "done")} className="text-xs px-2 py-1 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white inline-flex items-center gap-1" data-testid={`emp-task-done-${t.id}`}>
                                        <CheckCircle2 className="w-3 h-3" /> Done
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Leave & Loan history */}
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                <div className="bg-white p-8" data-testid="emp-leave-history">
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <div className="overline">My leaves</div>
                            <h3 className="mt-1 font-display font-bold text-xl tracking-tight text-ink">Recent requests</h3>
                        </div>
                        <Link to="/leaves" className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-klein" data-testid="emp-leaves-link">
                            All →
                        </Link>
                    </div>
                    {data.leaves.length === 0 ? (
                        <div className="text-sm text-ink/40 py-6 border-t border-ink/5">No leave requests yet.</div>
                    ) : (
                        <ul className="space-y-1">
                            {data.leaves.slice(0, 6).map((l) => (
                                <li key={l.id} className="flex items-center justify-between py-2 border-b border-ink/5 text-sm" data-testid={`emp-leave-${l.id}`}>
                                    <div className="min-w-0">
                                        <div className="text-ink truncate">{l.leave_type} · {l.start_date} → {l.end_date}</div>
                                        {l.reason && <div className="text-xs text-ink/50 truncate">{l.reason}</div>}
                                    </div>
                                    <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${LEAVE_PILL[l.status] || "bg-ink/5 text-ink/70 border-ink/10"}`}>{(l.status || "").replace(/_/g, " ")}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="bg-white p-8" data-testid="emp-loan-history">
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <div className="overline">My loans / advances</div>
                            <h3 className="mt-1 font-display font-bold text-xl tracking-tight text-ink">Recent records</h3>
                        </div>
                        <Link to="/my/loan-ledger" className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-klein" data-testid="emp-loans-link">
                            Full ledger →
                        </Link>
                    </div>
                    {data.loans.length === 0 ? (
                        <div className="text-sm text-ink/40 py-6 border-t border-ink/5">No loans or advances yet.</div>
                    ) : (
                        <ul className="space-y-1">
                            {data.loans.slice(0, 6).map((l) => (
                                <li key={l.id} className="flex items-center justify-between py-2 border-b border-ink/5 text-sm" data-testid={`emp-loan-${l.id}`}>
                                    <div className="min-w-0">
                                        <div className="text-ink truncate capitalize">{l.type || "loan"} · {fmt(l.amount)}</div>
                                        <div className="text-xs text-ink/50">Balance {fmt(l.balance)} · {l.type === "advance" ? "Single" : `${l.tenure_months}m`}</div>
                                    </div>
                                    <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${LOAN_PILL[l.status] || "bg-ink/5 text-ink/70 border-ink/10"}`}>{(l.status || "").replace(/_/g, " ")}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Performance summary */}
            <div className="mt-12 bg-white border border-ink/10 p-8" data-testid="emp-performance-card">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="overline">Your assessment</div>
                        <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-2">
                            <Award className="w-5 h-5 text-klein" /> Performance
                        </h3>
                    </div>
                    <Link to="/my/performance" className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-klein" data-testid="emp-performance-link">
                        Full history →
                    </Link>
                </div>
                {(() => {
                    const finalized = (data.performance || []).filter((r) => r.status === "finalized");
                    const inProgress = (data.performance || []).filter((r) => r.status !== "finalized" && r.status !== "rejected");
                    const latest = finalized[0];
                    if (!latest && inProgress.length === 0) {
                        return <div className="text-sm text-ink/40 py-4 border-t border-ink/5">No performance reviews on file yet.</div>;
                    }
                    return (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10 mt-2">
                            <div className="bg-white p-5"><div className="overline">Latest score</div><div className="mt-2 font-display font-black text-2xl text-ink tabular-nums">{latest?.normalized_score ?? "—"}{latest ? " / 100" : ""}</div></div>
                            <div className="bg-white p-5"><div className="overline">Latest rating</div><div className="mt-2 font-display font-black text-2xl text-ink">{latest?.rating || "—"}</div></div>
                            <div className="bg-klein text-white p-5"><div className="overline !text-white/70">In progress</div><div className="mt-2 font-display font-black text-2xl tabular-nums">{inProgress.length}</div></div>
                        </div>
                    );
                })()}
            </div>

            {/* Recent new hires (company-wide) */}
            <div className="mt-12 bg-white border border-ink/10 p-8" data-testid="emp-recent-hires">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="overline">Welcome aboard</div>
                        <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink">New colleagues</h3>
                    </div>
                    <div className="text-xs text-ink/50">Across the whole company</div>
                </div>
                {data.recentHires.length === 0 ? (
                    <div className="text-sm text-ink/40 py-6 border-t border-ink/5">No recent hires.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.recentHires.slice(0, 6).map((e) => (
                            <div key={e.id} className="flex items-center gap-3 border border-ink/10 p-3" data-testid={`emp-new-hire-${e.id}`}>
                                <div className="w-11 h-11 bg-ink/10 overflow-hidden shrink-0">
                                    {e.photo ? <img src={e.photo} alt="" className="w-full h-full object-cover" />
                                        : <div className="w-full h-full flex items-center justify-center font-display font-bold text-ink/60 text-sm">{(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}</div>}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-ink truncate">{e.first_name} {e.last_name}</div>
                                    <div className="text-xs text-ink/50 truncate">{e.position || "—"}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <PassportPhotoUploader
                open={passportOpen}
                onClose={() => setPassportOpen(false)}
                employeeId={emp.id}
                currentPhotoUrl={emp.passport_photo_path}
                onSaved={loadEmployee}
            />
        </div>
    );
}

function KPI({ label, value, icon: Icon, testid, accent }) {
    return (
        <div className={`p-6 ${accent ? "bg-klein text-white" : "bg-white"}`} data-testid={testid}>
            <div className="flex items-start justify-between">
                <div className={`overline ${accent ? "!text-white/70" : ""}`}>{label}</div>
                <div className={`w-8 h-8 flex items-center justify-center ${accent ? "bg-white/15" : "border border-ink/10 text-ink/60"}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                </div>
            </div>
            <div className="mt-6 font-display font-black text-3xl tracking-tighter tabular-nums truncate">{value}</div>
        </div>
    );
}
